<template>

<div class="menu">

    <template v-if="$route.meta.isSession">

        <com-item text="Settings" route="SessionSettings" :params="{ initial: false }" />
        <com-item text="Syncs" route="SessionSyncs" />
        <com-item text="Patreon" route="SessionPatreon" />
        <com-item text="Perks" :is-toggle="true" :active="isPatreonExpanded" v-on:click="isPatreonExpanded = !isPatreonExpanded" />
        
        <div class="menu-child" v-if="isPatreonExpanded">

            <com-item :indent="1" :gap="0" text="Daily Group Syncs" route="SessionPatreonSyncs" v-if="perks.groupsyncs" />
            <com-item :indent="1" text="Early Access" route="SessionPatreonAccess" v-if="perks.earlyaccess" />
            <com-item :indent="1" text="External Domains" route="SessionPatreonExternaldomains" v-if="perks.externaldomains" />
            <com-item :indent="1" text="Feature Voting" route="SessionPatreonVoting" v-if="perks.featurevoting" />
            <com-item :indent="1" text="Speed Boosts" route="SessionPatreonBoosts" v-if="perks.speedboosts" />
            <com-item :indent="1" text="Subdomains" route="SessionPatreonSubdomains" v-if="perks.subdomains" />

        </div>

        <com-item text="Logout" route="Logout" />

    </template>

    <template v-if="$route.meta.isCommunity">

        <com-item text="Patreon" route="CommunityPatreon" />

        <com-item text="Feature Voting" route="CommunityVoting" />

        <com-item text="Early Access" route="CommunityAccess" />

    </template>

    <template v-if="$route.meta.isBGG">

        <com-item text="Games" route="BGG" :count="128249" />

        <com-title text="GeekGroup" />

        <com-item text="Highest Rated" route="bggSearch" />
        <com-item text="Most Owned" route="bggSearch" />
        <com-item text="Most Played" route="bggSearch" />

        <com-title text="Browse" />

        <com-item text="Categories" route="bggSearch" />
        <com-item text="Themes" route="bggSearch" />

    </template>

    <template v-if="$route.meta.isProfile">

        <com-item text="Collection" route="Collection" :count="contextCount.collection" />
        <com-item text="Ratings" route="Ratings" :count="contextCount.ratings" />
        <com-item text="Plays" route="Plays" :count="contextCount.plays" />
        <com-item text="Lists" route="Lists" :count="contextCount.lists" />
        <com-item text="Members" route="Users" v-if="isGroup" :count="contextCount.users" />
        <com-item text="Buddies" route="Users" v-if="isBuddies" :count="contextCount.users" />
        <com-item text="Insights" :is-toggle="true" :active="isInsightsExpanded" v-on:click="isInsightsExpanded = !isInsightsExpanded" />

        <div class="menu-child" v-if="!isMinimised && isInsightsExpanded">

            <com-item text="Games" route="InsightsGames" :indent="1" v-if="isUser" />
            
            <com-title text="Collection" />

            <com-item text="Overview" route="InsightsCollection" :indent="1" :gap="0" class="must-be-exact" />
            <com-item text="Size" :first="true" route="InsightsSize" :indent="2" v-if="isUser" />
            <com-item :text="shelfName" route="InsightsShelf" :indent="2" v-if="isUser" />
            <com-item text="Worth" route="InsightsWorth" :indent="2" v-if="isUser" />

            <com-title text="Plays" />

            <com-item text="Overview" route="InsightsPlays" :indent="1" class="must-be-exact" />
            <com-item text="Friends & Foes" :first="true" route="InsightsPlayers" :indent="2" v-if="isUser" />
            <com-item text="H-index" route="InsightsHindex" :indent="2" v-if="isUser" />
            <com-item :text="track.name" :first="index === 0" route="InsightsHindexIndex" :params="{ index: track.slug }" :indent="3" v-for="(track, index) in tracking" :key="track.slug" />
            <com-item text="Streaks & Records" route="InsightsRecords" :indent="2" :gap="(isUser) ? tracking.length : 0" />
            
            <com-title text="Other" />

            <com-item text="Ratings" route="InsightsRatings" :indent="1" :gap="(isUser) ? 4 : 1" />
            <com-item text="Members" route="InsightsMembers" :indent="1" v-if="isGroup" :gap="4" />
            <com-item text="Buddies" route="InsightsMembers" :indent="1" v-if="isBuddies" :gap="4" />

        </div>

        <com-item text="Activity" route="Activity" />
        <com-item text="Tools" route="Tools" v-show="isUser" />

        <div class="menu-buttons">
            
            <core-button v-on:click.native="onManageClick" theme="blue" :iconLeft="true" icon="pencil" text="Edit group" class="context-action" v-if="canDoSomething" />
                
            <core-button v-on:click.native="onJoinClick" theme="blue" :iconLeft="true" icon="user-plus" class="context-action" text="Join group" v-if="canJoin" />
                
            <core-button v-on:click.native="onLeaveClick" theme="blue" :iconLeft="true" icon="user-minus" class="context-action" text="Leave group" v-if="canLeave" />

            <core-button v-on:click.native="onAddClick" theme="blue" :iconLeft="true" icon="user-plus" text="Add member" v-if="canDoSomething" />

            <core-button v-on:click.native="onDeleteClick" theme="red" :iconLeft="true" icon="trash" text="Delete group" v-if="canDoSomething" />

        </div>

    </template>

    <div class="notice" v-if="hasNotice">
        <i class="fa-solid fa-users-gear"></i>
        <span>The group is queued to update insights and lists after recent changes.</span>
    </div>

    <div class="notice is-data" v-if="showDataNotice" v-on:click="onDataClick">
        <i class="fa-solid fa-rotate"></i>
        <span>{{ you }} {{ frequency }}, last sync was {{ updated | formatDate('Do MMMM YYYY') }}.<small>Click to read more about data syncs.</small></span>
    </div>

</div>

</template>

<script>

import comItem from './menu/Item'
import comTitle from './menu/Title'

export default {

    props: ['isMinimised'],

    components: {
        'com-item': comItem,
        'com-title': comTitle
    },

    data: function() {

        return {
            timer: false,
            isInsightsExpanded: false,
            isPatreonExpanded: false
        }

    },

    computed: {

        showDataNotice: function() {

            return this.isUser && !this.hasNotice && !this.$route.meta.isSession && !this.$route.meta.isCommunity && (!this.isSelf || !this.$store.getters['context'].upgraded)

        },

        you: function() {

            return (this.isSelf) ? 'You update' : 'This user updates'

        },

        updated: function() {

            return this.$store.getters['context'].date.sync

        },

        frequency: function() {

            if (this.$store.getters['context'].upgraded) {

                return 'daily'

            } else if (this.$store.getters['context'].date.registered) {

                return 'weekly'

            } else {

                return 'monthly'

            }

        },

		upgraded: function() {

			return this.$store.getters['session/upgraded']

		},

        hasNotice: function() {

            return this.$store.getters['context/queued']

        },

        canDoSomething: function() {

			return this.canDelete || this.canAdd || this.canEdit

		},

		canJoin: function() {

			return this.$store.getters['permissions/group/join']

		},

		canLeave: function() {

			return this.$store.getters['permissions/group/leave'] 

		},

		canDelete: function() {

			return this.$store.getters['permissions/group/delete']

		},

        hasPerks: function() {

            return this.$_.filter(this.$store.getters['session/user/perks'], function(value, key) {

                return value === 1 && !this.$_.contains(['manualsyncs', 'dailysyncs', 'groupurl'], key)

            }.bind(this)).length

        },

        perks: function() {

            return this.$store.getters['session/user/perks']

        },

        tracking: function() {

            return (this.$store.getters['context/hindex']) ? this.$store.getters['context/hindex'].tracking : []

        },
    
        contextCount: function() {

			return this.$store.getters['context'].count

		},

        shelfName: function() {

			return (this.$store.getters['session/config'].lists.unplayed.type === 'shame') ? 'Shelf of Shame' : 'Shelf of Opportunity'

		}
    
    },

    created: function() {

        this.checkPath()

        if (this.hasNotice) {

             this.queueCheck()

        }

    },

    watch: {

        $path: function() {

            this.checkPath()
            
        },

        hasNotice: function(n) {

            if (n) {

                this.queueCheck()

            } else {

                clearTimeout(this.timer)

            }

        }

    },

    methods: {

		onPageClick: function(name, e) {

			e.stopPropagation()

			this.$pubsub.$emit('open.modal', name)

		},

        onPrivacyClick: function(e) {

            this.onPageClick('appPrivacy', e)

        },

        onTermsClick: function(e) {

            this.onPageClick('appTerms', e)

        },

        onDataClick: function() {

            this.$pubsub.$emit('open.modal', 'appSyncs')

        },

        checkPath: function() {

            var n = this.$route.path

            this.isPatreonExpanded = n.indexOf('patreon/') !== -1
            this.isInsightsExpanded = n.indexOf('insights/') !== -1

        },

        queueCheck: function() {

            /*
            this.timer = this.$_.delay(function() {

                this.$store.dispatch('reverify').then(function() {

                    if (this.hasNotice) this.queueCheck()

                }.bind(this))

            }.bind(this), 60000)
            */

        },

        onAddClick: function() {

			this.$pubsub.$emit('open.modal', 'groupAdd')

		},

		onDeleteClick: function() {

			this.$pubsub.$emit('open.modal', 'groupDelete')

		},

		onManageClick: function() {

			this.$pubsub.$emit('open.modal', 'groupEdit')

		},

		onLeaveClick: function() {

			this.$pubsub.$emit('open.modal', 'groupLeave')

		},

		onJoinClick: function() {

			if (this.loggedin) {

				this.$pubsub.$emit('open.modal', 'groupJoin')

			} else {

				this.$pubsub.$emit('open.modal', 'userLogin')

			}

		}

    }

}

</script>

<style scoped>

.notice {
    padding: 10px;
    color: #fff;
    border-radius: 10px;
    margin: 20px;
    font-size: 14px;
    line-height: 16px;
    font-weight: 500;
    background-color: rgba(255, 255, 255, 0.1);
    display: grid;
    grid-template-columns: auto 1fr;
    grid-gap: 10px;
    align-items: center;
}

.is-mobile .notice {
    padding: 10px 10px;
    font-size: 14px;
    grid-gap: 10px;
    font-weight: 400;
    line-height: 18px;
}

.notice.is-data {
    cursor: pointer;
}

.notice.is-data:hover {
    background-color: #3b71a7;
}

.notice i {
    font-size: 16px;
    text-align: center;
    background-color: rgba(0, 0, 0, 0.15);
    border-radius: 50%;
    height: 40px;
    line-height: 40px;
    width: 40px;
}

.notice small {
    font-size: 12px;
    line-height: 14px;
    font-weight: 400;
    display: block;
    opacity: 0.75;
    margin-top: 1px;
}

.is-mobile .notice i {
    font-size: 24px;
}

.menu {
    padding: 0px 0px 0px 0px;
    flex-grow: 1;
    display: flex;
    user-select: none;
    flex-direction: column;
    overflow: auto;
}

.is-minimised .menu {
    padding: 0px;
}

.menu-buttons {
    display: flex;
    flex-direction: column;
    padding: 10px 10px 8px 10px;
    flex-grow: 1;
    justify-content: flex-end;
}

.menu-buttons >>> .button {
    text-align: left;
    margin-bottom: 4px;
}

.menu-buttons >>> .button-icon {
    width: 32px;
    font-size: 16px;
}

.menu-buttons >>> .button.theme-red {
    background-color: #b64b4b;
}

.menu-buttons >>> .button.theme-red:hover {
    background-color: #ad4141;
}

.menu-child {
    background-color: #274F65;
    padding: 0px 0px 15px 0px;
    user-select: none;
}

.menu-child >>> .title {
    opacity: 0.3;
    margin-top: 15px;
    padding: 0px 20px;
    font-size: 12px;
    margin-bottom: 4px;
}

.is-mobile .menu-child >>> .title {
    padding: 0px 20px;
}

.menu-child >>> .title:first-child {
    margin-top: 10px;
}

.menu-child >>> .title-text {
    font-weight: 400;
}

.menu-child >>> .item {
    border: 0px;
    font-size: 12px;
    padding: 6px 20px;
    opacity: 0.75;
}

.is-mobile .menu-child >>> .item {
    padding: 6px 20px;
}

.menu-child >>> .item.router-link-exact-active {
    opacity: 1;
}

</style>
